import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Keyboard, MapPin, ScanLine } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import { scanOffcutTransfer } from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import { detectScanCodeKind, normalizeScannedCode } from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { getStoredUser } from '../utils/session';

const AUTO_SCAN_DELAY_MS = 500;

function ScanOffcutTransfer() {
  const inputRef = useRef<HTMLInputElement>(null);
  const resetTimerRef = useRef<number>(0);
  const scanTimerRef = useRef<number>(0);
  const [scanValue, setScanValue] = useState('');
  const [ocsf, setOcsf] = useState('');
  const [locCode, setLocCode] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const operator = getStoredUser();
  const operatorDisplay = operator?.full_name || operator?.username || operator?.emp_no || '员工';
  const step = !ocsf ? 'offcut' : !locCode ? 'location' : 'details';
  const progressSteps = [
    {
      complete: Boolean(ocsf),
      current: step === 'offcut',
      icon: ScanLine,
      label: '料头',
    },
    {
      complete: Boolean(locCode),
      current: step === 'location',
      icon: MapPin,
      label: '库位',
    },
    {
      complete: Boolean(successMessage),
      current: step === 'details' && !successMessage,
      icon: CheckCircle2,
      label: '确认',
    },
  ];

  usePersistentFocus(
    inputRef,
    Boolean(operator) && !successMessage && step !== 'details',
    `${step}:${ocsf}:${locCode}`,
  );

  useEffect(
    () => () => {
      window.clearTimeout(resetTimerRef.current);
      window.clearTimeout(scanTimerRef.current);
    },
    [],
  );

  if (!operator) {
    navigateTo('/login', true);
    return null;
  }

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function resetFlow() {
    clearScanInput();
    setOcsf('');
    setLocCode('');
    setNote('');
    setSubmitting(false);
    setSuccessMessage('');
  }

  function scheduleReset() {
    window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(() => {
      resetFlow();
    }, 2000);
  }

  async function handleScan(rawCode?: string) {
    window.clearTimeout(scanTimerRef.current);
    const code = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);
    const kind = detectScanCodeKind(code);

    if (!code || submitting || successMessage) {
      return;
    }

    if (step === 'offcut') {
      if (kind === 'offcut') {
        setOcsf(code);
        clearScanInput();
        notifySuccess(`料头 ${code}`);
        return;
      }
      if (kind === 'location') {
        notifyError('先扫料头 QR,别扫库位');
      } else if (kind === 'equipment') {
        notifyError('设备扫码功能尚未开放');
      } else if (kind === 'rawMaterial') {
        notifyError('v1.0 不处理原材料批次码');
      } else {
        notifyError('这里要扫料头 QR');
      }
      clearScanInput();
      return;
    }

    if (step === 'location') {
      if (kind === 'location') {
        setLocCode(code);
        clearScanInput();
        notifySuccess(`目标库位 ${code}`);
        return;
      }
      if (kind === 'offcut') {
        notifyError('料头已经扫过了，请继续扫目标库位');
      } else {
        notifyError('这里要扫目标库位');
      }
      clearScanInput();
    }
  }

  function scheduleScan(rawCode: string) {
    if (submitting || successMessage || step === 'details') {
      return;
    }

    setScanValue(rawCode);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      void handleScan(inputRef.current?.value ?? rawCode);
    }, AUTO_SCAN_DELAY_MS);
  }

  async function handleSubmit() {
    if (submitting || successMessage) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await scanOffcutTransfer({
        loc_code: locCode,
        note: note.trim() || undefined,
        ocsf,
      });

      const message = `从 ${response.data.from_loc || '原库位'} 调到 ${response.data.to_loc} 成功`;
      setSuccessMessage(message);
      notifySuccess(message);
      scheduleReset();
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '调库失败'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PdaPage
      actionLabel="重置"
      onAction={resetFlow}
      onBack={() => navigateTo('/home')}
      pageClassName="pre-inbound-page"
      subtitle="料头码 → 目标库位"
      title="料头调库"
    >
      <section className="pre-progress-card">
        <div className="pre-progress-title">当前进度</div>
        <div className="pre-step-track">
          {progressSteps.map((item) => {
            const StepIcon = item.icon;
            const className = [
              'pre-step',
              item.complete ? 'pre-step--complete' : '',
              item.current ? 'pre-step--current' : '',
              item.complete ? 'pre-step--line-complete' : '',
            ].filter(Boolean).join(' ');

            return (
              <div className={className} key={item.label}>
                <div className="pre-step__line" />
                <div className="pre-step__icon">
                  <StepIcon aria-hidden="true" size={15} strokeWidth={2.4} />
                </div>
                <div className="pre-step__label">{item.label}</div>
              </div>
            );
          })}
        </div>
        <div className="pre-progress-count">
          <div className="pre-progress-count__label">目标</div>
          <div className="pre-progress-count__value">{locCode || '--'}</div>
          <div className="pre-progress-count__sub">库位</div>
        </div>
      </section>

      <section className="pre-info-card">
        <div className="pre-info-grid">
          <div className="pre-info-cell">
            <div className="pre-label">操作人</div>
            <div className="pre-value">
              {operatorDisplay}{operator.emp_no ? ` · ${operator.emp_no}` : ''}
            </div>
          </div>
          <div className="pre-info-cell">
            <div className="pre-label">目标库位</div>
            <div className="pre-value">{locCode || (ocsf ? '待扫描' : '先扫料头')}</div>
          </div>
        </div>
        <button className="pre-order-card" disabled={!ocsf} type="button">
          <div>
            <div className="pre-label">料头码</div>
            <div className="pre-order-title">{ocsf || '等待扫码'}</div>
            <div className="pre-order-sub">{locCode ? `调入 ${locCode}` : '扫码后继续扫目标库位'}</div>
          </div>
          <span className="pre-chevron">›</span>
        </button>
      </section>

      {successMessage ? (
        <section className="panel success-panel">
          <div className="success-icon">✓</div>
          <div className="success-text">{successMessage}</div>
          <div className="panel-hint">2 秒后自动回到“等扫料头”。</div>
        </section>
      ) : step !== 'details' ? (
        <section className="pre-scan-card" key={`scan-step-${step}-${ocsf}-${locCode}`}>
          <div className="panel-title">{step === 'offcut' ? '先扫料头 QR' : '再扫目标库位'}</div>
          <div className="pre-scan-box">
            <ScanLine className="pre-scan-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
            <input
              autoComplete="off"
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              className="pre-scan-input"
              id="scan-transfer-code"
              inputMode="none"
              onInput={(event) => scheduleScan(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleScan(event.currentTarget.value);
                }
              }}
              placeholder="扫描后按回车..."
              ref={inputRef}
              spellCheck={false}
            />
            <Keyboard className="pre-keyboard-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
          </div>
        </section>
      ) : (
        <section className="pre-entry-card" key={`details-step-${ocsf || 'empty'}-${locCode || 'empty'}`}>
          <div className="pre-entry-head">
            <div>
              <div className="pre-entry-title">确认调库</div>
              <div className="pre-entry-sub">{ocsf} → {locCode}</div>
            </div>
            <span className="pre-chevron">›</span>
          </div>
          <label className="pre-note-field" htmlFor="scan-transfer-note">
            <span>备注(可选)</span>
            <input
              id="scan-transfer-note"
              onChange={(event) => setNote(event.target.value)}
              placeholder="例如: 锯床预上料"
              value={note}
            />
            <span className="pre-note-edit" aria-hidden="true" />
          </label>
          <div className="button-row" style={{ padding: 9 }}>
            <button className="secondary-btn" onClick={resetFlow} type="button">
              重置本次
            </button>
            <button className="primary-btn" disabled={submitting} onClick={() => void handleSubmit()} type="button">
              {submitting ? '提交中...' : '提交调库'}
            </button>
          </div>
        </section>
      )}
    </PdaPage>
  );
}

export default ScanOffcutTransfer;
